import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import './PortfolioLoader.css';

/**
 * PortfolioLoader — NeoLeaf Style Liquid Wave Fill & Cinematic Camera Zoom
 * 
 * Features:
 *   1. Authentic rolling water waves filling from bottom to top.
 *   2. Ultra-heavy typography in 'Archivo Black' (matching NeoLeaf reference).
 *   3. Discreet bottom-right 'loading... X %' counter.
 *   4. Cinematic Camera Zoom Through transition: at 100%, zooms dramatically (22x scale)
 *      through the letters to unveil the portfolio homepage.
 *
 * Props:
 *   @param {string} [brandText='Sharief'] - Name to display ('Sharief' matches NeoLeaf case styling).
 *   @param {boolean} [alwaysShow=true] - Shows on refresh (default for active dev/testing).
 *   @param {boolean} [oncePerSession=false] - Only show once per session in production.
 *   @param {number} [duration=2800] - Duration in ms for the wave rise.
 *   @param {number} [holdDuration=250] - Brief hold at 100% before camera zoom.
 *   @param {number} [zoomDuration=950] - Duration in ms for the camera zoom-through.
 *   @param {string} [fillColor] - Wave liquid color (defaults to theme primary).
 */
const PortfolioLoader = ({
  brandText = 'Sharief',
  alwaysShow = true,
  oncePerSession = false,
  duration = 2800,
  holdDuration = 250,
  zoomDuration = 950,
  fillColor,
  sessionKey = 'portfolio_loader_seen',
  onComplete,
}) => {
  const muiTheme = useTheme();

  // Primary color: theme primary (rgb(255, 78, 35))
  const primaryColor = fillColor || muiTheme?.palette?.primary?.main || 'rgb(255, 78, 35)';

  const shouldRun = () => {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('skip_intro') === 'true') return false;
    if (urlParams.get('intro') === 'true' || alwaysShow) return true;

    if (oncePerSession) {
      try {
        return !sessionStorage.getItem(sessionKey);
      } catch (e) {
        return true;
      }
    }
    return true;
  };

  const [active, setActive] = useState(shouldRun);
  const [progress, setProgress] = useState(0);
  const [isZooming, setIsZooming] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const completedRef = useRef(false);

  // Trigger the dramatic camera zoom-through exit
  const handleZoomExit = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;

    // Trigger massive 22x scale camera zoom into the letters
    setIsZooming(true);
    setIsExiting(true);

    if (oncePerSession) {
      try {
        sessionStorage.setItem(sessionKey, 'true');
      } catch (e) {
        // Storage access fallback
      }
    }

    setTimeout(() => {
      setActive(false);
      document.body.style.overflow = '';
      if (onComplete) onComplete();
    }, zoomDuration);
  }, [zoomDuration, onComplete, oncePerSession, sessionKey]);

  // Lock body scroll while loader is visible
  useEffect(() => {
    if (active) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [active]);

  // Synchronized requestAnimationFrame wave progression
  useEffect(() => {
    if (!active) return;

    let start = null;
    let rafId = null;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const rawProgress = Math.min(elapsed / duration, 1);

      // Natural fluid easeInOutQuad acceleration
      const easeProgress = rawProgress < 0.5
        ? 2 * rawProgress * rawProgress
        : 1 - Math.pow(-2 * rawProgress + 2, 2) / 2;

      setProgress(easeProgress * 100);

      if (rawProgress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setProgress(100);
        // Brief pause at 100%, then trigger dramatic camera zoom into portfolio
        setTimeout(() => {
          handleZoomExit();
        }, holdDuration);
      }
    };

    rafId = requestAnimationFrame(step);

    // Hard failsafe timer
    const failsafe = setTimeout(() => {
      if (!completedRef.current) {
        handleZoomExit();
      }
    }, 5500);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(failsafe);
    };
  }, [active, duration, holdDuration, handleZoomExit]);

  // Keyboard navigation: Escape / Space / Enter to skip
  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleZoomExit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, handleZoomExit]);

  if (!active) return null;

  // Liquid vertical level in SVG viewBox coordinates (0 to 300)
  // At progress = 0:   waveY = 240 (completely below the letters)
  // At progress = 50:  waveY = 140 (exact midpoint of letters)
  // At progress = 100: waveY = 40  (completely above the letters)
  const waveY = 240 - (progress / 100) * 200;

  // Real fluid rolling wave path spanning -1200px to +3000px
  // Period = 600px, amplitude +/- 20px, filled down to y=550
  const realWaterWavePath = `
    M -1200 0
    Q -1050 -20, -900 0 T -600 0
    Q -450 -20, -300 0 T 0 0
    Q 150 -20, 300 0 T 600 0
    Q 750 -20, 900 0 T 1200 0
    Q 1350 -20, 1500 0 T 1800 0
    Q 1950 -20, 2100 0 T 2400 0
    Q 2550 -20, 2700 0 T 3000 0
    L 3000 550
    L -1200 550
    Z
  `;

  return (
    <aside
      aria-label="Portfolio Loading Animation"
      aria-live="polite"
      className={`portfolio-loader-overlay ${isExiting ? 'is-exiting' : ''}`}
    >
      {/* Ambient background depth vignette */}
      <div className="loader-ambient-vignette" />

      {/* Discreet Skip Button */}
      <button
        type="button"
        className="loader-skip-action"
        onClick={handleZoomExit}
        aria-label="Skip Loading Screen"
      >
        <span>Skip</span>
        <span className="loader-esc-tag">ESC</span>
      </button>

      {/* Main Stage: Zooms dramatically into the camera lens when completed */}
      <div className={`loader-stage-container ${isZooming ? 'is-zooming' : ''}`}>
        {/* SVG Water Wave Typography Display */}
        <div className="loader-svg-frame">
          <svg
            className="loader-wave-svg"
            viewBox="0 0 1000 300"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {/* Text Clipping Mask in 'Archivo Black' font */}
              <clipPath id="neoleaf-text-mask">
                <text
                  x="500"
                  y="155"
                  className="loader-text-glyph"
                >
                  {brandText}
                </text>
              </clipPath>

              {/* Back Wave: Harmonized Primary Liquid with depth opacity */}
              <linearGradient id="water-gradient-back" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={primaryColor} stopOpacity="0.55" />
                <stop offset="100%" stopColor={primaryColor} stopOpacity="0.75" />
              </linearGradient>

              {/* Front Wave: Pure Matching Primary Liquid */}
              <linearGradient id="water-gradient-front" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={primaryColor} stopOpacity="0.9" />
                <stop offset="100%" stopColor={primaryColor} stopOpacity="1" />
              </linearGradient>
            </defs>

            {/* Layer 1: Base Dark Charcoal Gray Text (#38383a - Matches Screenshot 1 & 2) */}
            <text
              x="500"
              y="155"
              className="loader-text-glyph"
              fill="#38383a"
            >
              {brandText}
            </text>

            {/* Layer 2: Liquid Water Waves clipped strictly inside the letter shapes */}
            <g clipPath="url(#neoleaf-text-mask)">
              {/* Rising Liquid Container */}
              <g transform={`translate(0, ${waveY})`}>
                {/* Secondary Harmonic Wave */}
                <path
                  className="wave-water-path wave-water-back"
                  d={realWaterWavePath}
                  fill="url(#water-gradient-back)"
                />

                {/* Primary Water Wave */}
                <path
                  className="wave-water-path wave-water-front"
                  d={realWaterWavePath}
                  fill="url(#water-gradient-front)"
                />
              </g>
            </g>
          </svg>
        </div>

        {/* Bottom-Right Discreet Counter — 100% matched to the liquid fill color */}
        <div className="loader-sub-footer">
          <div className="loader-neoleaf-counter" style={{ color: primaryColor }}>
            <span className="counter-dots" style={{ color: primaryColor, opacity: 0.85 }}>
              loading...
            </span>
            <span
              className="counter-value"
              style={{
                color: primaryColor,
                textShadow: `0 0 14px ${primaryColor}`,
              }}
            >
              {Math.round(progress)}
            </span>
            <span className="counter-symbol" style={{ color: primaryColor }}>
              %
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default PortfolioLoader;
